import React from 'react';
import { ChevronDown, Play, Sparkles, X } from 'lucide-react';
import type { IntellioAssistantWorkItemRecord } from '@triedge/domain-core';

type AssistantPromptAction = NonNullable<IntellioAssistantWorkItemRecord['assistantPrompt']>['suggestedActions'][number];

export interface AssistantProactiveTrayProps {
  workItem: IntellioAssistantWorkItemRecord;
  isCollapsed: boolean;
  onToggleCollapsed: () => void;
  onDismiss: () => void;
  onPrimaryAction: () => void;
  onSuggestedAction: (action: AssistantPromptAction) => void;
}

const resolvePrimaryLabel = (workItem: IntellioAssistantWorkItemRecord): string => {
  const prompt = workItem.assistantPrompt;
  if (prompt?.seedQuestion) {
    return 'Run prompt';
  }
  if (prompt?.suggestedActions?.some((action) => String(action?.targetRoute ?? '').trim().length > 0)) {
    return 'Open route';
  }
  return 'Open';
};

export const AssistantProactiveTray: React.FC<AssistantProactiveTrayProps> = ({
  workItem,
  isCollapsed,
  onToggleCollapsed,
  onDismiss,
  onPrimaryAction,
  onSuggestedAction,
}) => {
  const prompt = workItem.assistantPrompt;
  const summary = String(prompt?.summary ?? workItem.summary ?? 'New proactive recommendation').trim();
  const seedQuestion = String(prompt?.seedQuestion ?? '').trim();
  const suggestedActions = (prompt?.suggestedActions ?? [])
    .filter((action) => String(action?.label ?? '').trim().length > 0)
    .slice(0, 3);

  return (
    <section
      className="relative overflow-hidden rounded-[20px] border border-amber-200/80 bg-[linear-gradient(135deg,rgba(255,247,237,0.98),rgba(255,251,235,0.98)_58%,rgba(255,255,255,0.98))] px-3 py-3 shadow-[0_18px_40px_rgba(217,119,6,0.14)]"
      data-testid="ai-proactive-prompt-banner"
      data-ai-proactive-tone="priority"
      data-work-item-id={workItem.id}
    >
      <div className="pointer-events-none absolute inset-y-0 left-0 w-1.5 bg-[linear-gradient(180deg,#F59E0B,#EA580C)]" />
      <div className="pointer-events-none absolute -right-6 top-0 h-24 w-24 rounded-full bg-amber-200/40 blur-2xl" />
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[linear-gradient(135deg,#F59E0B,#EA580C)] text-white shadow-[0_10px_24px_rgba(234,88,12,0.22)]">
          <Sparkles size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-700/80">
                Proactive recommendation
              </p>
              <p className="mt-1 text-[13px] font-semibold leading-5 text-slate-900">
                {summary}
              </p>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                className="rounded-full border border-amber-200/80 bg-white/90 p-1.5 text-amber-700/70 transition-colors hover:border-amber-300 hover:bg-amber-50 hover:text-amber-800"
                onClick={onToggleCollapsed}
                aria-label={isCollapsed ? 'Expand proactive recommendation' : 'Collapse proactive recommendation'}
                data-testid="ai-proactive-prompt-toggle"
              >
                <ChevronDown size={14} className={isCollapsed ? 'rotate-180 transition-transform' : 'transition-transform'} />
              </button>
              <button
                type="button"
                className="rounded-full border border-amber-200/80 bg-white/90 p-1.5 text-amber-700/70 transition-colors hover:border-strawberry/20 hover:text-strawberry"
                onClick={onDismiss}
                aria-label="Dismiss proactive recommendation"
                data-testid="ai-proactive-prompt-dismiss"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          {!isCollapsed ? (
            <>
              {seedQuestion ? (
                <p className="mt-2 text-[12px] leading-5 text-slate-700/85">
                  {seedQuestion}
                </p>
              ) : null}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 rounded-full bg-[linear-gradient(135deg,#D97706,#B45309)] px-3 py-1.5 text-[11px] font-semibold text-white shadow-[0_10px_24px_rgba(180,83,9,0.18)] transition-all hover:translate-y-[-1px] hover:shadow-[0_14px_28px_rgba(180,83,9,0.24)]"
                  onClick={onPrimaryAction}
                  data-testid="ai-proactive-prompt-open"
                >
                  <Play size={13} />
                  {resolvePrimaryLabel(workItem)}
                </button>
                {suggestedActions.map((action, index) => (
                  <button
                    key={`${workItem.id}-suggested-action-${index + 1}`}
                    type="button"
                    className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50/80 px-3 py-1.5 text-[11px] font-semibold text-amber-800 transition-colors hover:bg-amber-100"
                    onClick={() => onSuggestedAction(action)}
                    data-testid={`ai-proactive-prompt-action-${index + 1}`}
                    title={String(action.targetRoute ?? action.prompt ?? '').trim() || undefined}
                  >
                    {String(action.label ?? '').trim()}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-full bg-[linear-gradient(135deg,#D97706,#B45309)] px-3 py-1.5 text-[11px] font-semibold text-white shadow-[0_10px_24px_rgba(180,83,9,0.18)] transition-all hover:translate-y-[-1px] hover:shadow-[0_14px_28px_rgba(180,83,9,0.24)]"
                onClick={onPrimaryAction}
                data-testid="ai-proactive-prompt-open"
              >
                <Play size={13} />
                {resolvePrimaryLabel(workItem)}
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
